import { useEffect, useState, type ReactNode } from 'react';
import { Fingerprint } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { fetchPasskeyAvailable } from '../../api/passkeyApi';
import { useFeature } from '../../lib/features';

// True once the server says passkeys are switched on AND this browser can
// run WebAuthn. Starts false, so the button never flashes in then vanishes.
export function usePasskeyAvailable(): boolean {
  const flagOn = useFeature('auth.passkeys');
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let live = true;
    fetchPasskeyAvailable().then((ok) => {
      if (live) setAvailable(ok);
    });
    return () => {
      live = false;
    };
  }, []);
  return available && flagOn;
}

interface PasskeyButtonProps {
  children: ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

// Renders nothing unless passkeys are usable, so callers can drop it in
// unconditionally.
export function PasskeyButton({ children, onClick, loading, disabled }: PasskeyButtonProps) {
  const available = usePasskeyAvailable();
  if (!available) return null;
  return (
    <Button type="button" variant="bordered" full loading={loading} disabled={disabled} onClick={onClick}>
      {!loading && <Fingerprint size={18} />}
      {children}
    </Button>
  );
}
